import { isOsanoConsentExpected } from "./mark-osano-consent-expected";

const OSANO_SEGMENT_READY_TIMEOUT_MS = 5_000;
const OSANO_SEGMENT_POLL_INTERVAL_MS = 50;
/** Time to allow the Osano client loader module to register before ungated init. */
const OSANO_CONSENT_EXPECTATION_GRACE_MS = 250;

type OsanoSegmentBridgeWaitContext = {
  initializedListener?: () => void;
  pollIntervalId?: number;
  resolve: () => void;
};

function isOsanoScriptOnPage(): boolean {
  return Boolean(
    typeof document !== "undefined" &&
      document.querySelector('script[src*="cmp.osano.com"]'),
  );
}

/**
 * True when Osano is already on the page, or the consent loader has registered
 * that it will inject soon (before any script tag exists).
 */
function isOsanoExpectedOrPresent(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean(isOsanoConsentExpected() || window.Osano || isOsanoScriptOnPage())
  );
}

function isOsanoSegmentBridgeReady(): boolean {
  return (
    typeof window.withCMP !== "undefined" &&
    typeof window.Osano?.cm?.getConsent === "function" &&
    typeof window.Osano?.cm?.addEventListener === "function"
  );
}

function finishAwait(context: OsanoSegmentBridgeWaitContext): void {
  if (context.pollIntervalId !== undefined) {
    window.clearInterval(context.pollIntervalId);
    context.pollIntervalId = undefined;
  }

  if (context.initializedListener) {
    document.removeEventListener(
      "osano-cm-initialized",
      context.initializedListener,
    );
    context.initializedListener = undefined;
  }

  context.resolve();
}

function tryToResolveAwait(context: OsanoSegmentBridgeWaitContext): boolean {
  if (isOsanoSegmentBridgeReady()) {
    finishAwait(context);
    return true;
  }

  return false;
}

/**
 * Waits briefly for the Osano consent loader to register (or scripts to appear)
 * before analytics decides between gated and ungated Segment load.
 */
function waitForOsanoConsentExpectation(): Promise<void> {
  if (typeof window === "undefined" || isOsanoExpectedOrPresent()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const deadline = Date.now() + OSANO_CONSENT_EXPECTATION_GRACE_MS;

    const pollIntervalId = window.setInterval(() => {
      if (isOsanoExpectedOrPresent() || Date.now() >= deadline) {
        window.clearInterval(pollIntervalId);
        resolve();
      }
    }, OSANO_SEGMENT_POLL_INTERVAL_MS);
  });
}

/**
 * Resolves when Osano CMP and the Segment bridge are ready, or after a timeout.
 */
function waitForOsanoSegmentBridge(): Promise<void> {
  if (typeof window === "undefined" || isOsanoSegmentBridgeReady()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const context: OsanoSegmentBridgeWaitContext = {
      pollIntervalId: undefined,
      resolve,
    };

    if (tryToResolveAwait(context)) {
      return;
    }

    const deadline = Date.now() + OSANO_SEGMENT_READY_TIMEOUT_MS;

    context.pollIntervalId = window.setInterval(() => {
      if (tryToResolveAwait(context)) {
        return;
      }

      if (Date.now() >= deadline) {
        finishAwait(context);
      }
    }, OSANO_SEGMENT_POLL_INTERVAL_MS);

    const initializedListener = () => {
      tryToResolveAwait(context);
    };

    context.initializedListener = initializedListener;

    document.addEventListener("osano-cm-initialized", initializedListener, {
      once: true,
    });
  });
}

export {
  isOsanoExpectedOrPresent,
  isOsanoSegmentBridgeReady,
  waitForOsanoConsentExpectation,
  waitForOsanoSegmentBridge,
};
